import api from '@/api';

/**
 * Typed client for `/admin/console/*`.
 *
 * Mirrors `trovepile-firebase-master/functions/src/types/admin.types.ts`. When
 * adding a new dangerous action, also add a matching `StepUpActionClass`
 * literal here so TypeScript can type-check the step-up-token flow end-to-end.
 */

export type SuperAdminRole = 'owner' | 'admin';

export interface SuperAdminMember {
  uid: string;
  email: string;
  role: SuperAdminRole;
  addedBy: string;
  addedAt: number;
  addedByEmail?: string;
  mfaEnrolled?: boolean;
  mfaCheckedAt?: number;
  lastActivityAt?: number;
}

export type StepUpActionClass =
  | 'team.promote'
  | 'team.demote'
  | 'team.signout'
  | 'billing.refund'
  | 'billing.planChange'
  | 'billing.scheduler'
  | 'billing.trialExtend'
  | 'account.delete'
  | 'account.deletionCancel'
  | 'account.passwordReset'
  | 'account.emailChange'
  | 'account.signOut'
  | 'account.discoverEdit'
  | 'account.impersonate'
  | 'ai.killSwitch'
  | 'ai.throttle'
  | 'publicApi.keyRevoke'
  | 'publicApi.keyRotate'
  | 'webhook.testPing'
  | 'vault.restore'
  | 'vault.purge'
  | 'vault.extend'
  | 'kill.global'
  | 'abuse.block'
  | 'abuse.readOnly'
  | 'betaFlag.toggle'
  | 'scheduler.trigger';

export interface AdminPendingAction {
  id: string;
  actionClass: StepUpActionClass;
  summary: string;
  payload: Record<string, unknown>;
  proposerUid: string;
  proposerEmail: string;
  approverUid?: string;
  approverEmail?: string;
  status: 'pending' | 'approved' | 'rejected' | 'expired' | 'executed';
  reason?: string;
  createdAt: number;
  expiresAt: number;
  approvedAt?: number;
  executedAt?: number;
  executionResult?: Record<string, unknown>;
}

export interface AdminAuditLogEntry {
  id: string;
  actorUid: string;
  actorEmail: string;
  actorRole: SuperAdminRole;
  action: string;
  actionClass?: StepUpActionClass;
  dryRun: boolean;
  stepUpUsed: boolean;
  twoPersonApproved: boolean;
  secondApproverUid?: string;
  secondApproverEmail?: string;
  targetAid?: string;
  targetUid?: string;
  before?: unknown;
  after?: unknown;
  reason?: string;
  ip?: string;
  ua?: string;
  status: 'success' | 'error';
  errorCode?: string;
  errorMessage?: string;
  at: number;
}

export interface AdminWhoamiServer {
  time: number;
  mfaEnforced: boolean;
  adminFeedConfigured: boolean;
}

export interface AdminWhoami {
  member: SuperAdminMember;
  server: AdminWhoamiServer;
}

interface Envelope<T> {
  status: 'success' | 'error';
  code?: string;
  message?: string;
  data?: T;
}

function unwrap<T>(env: Envelope<T>): T {
  if (env.status !== 'success' || env.data === undefined) {
    throw new AdminApiError(env.code ?? 'ADMIN_ERROR', env.message ?? 'Admin request failed.');
  }
  return env.data;
}

export class AdminApiError extends Error {
  constructor(public readonly code: string, message: string) {
    super(message);
    this.name = 'AdminApiError';
  }
}

interface DangerOpts {
  /** Required for any endpoint marked as dangerous server-side. */
  stepUpToken?: string;
}

function dangerHeaders(opts: DangerOpts | undefined): Record<string, string> {
  if (!opts?.stepUpToken) return {};
  return { 'X-Step-Up-Token': opts.stepUpToken };
}

// ------------------ Whoami / health ------------------

export async function getAdminWhoami(): Promise<AdminWhoami> {
  const { data } = await api.get<Envelope<AdminWhoami>>('/admin/console/whoami');
  return unwrap(data);
}

// ------------------ Step-up ------------------

export interface StepUpResponse {
  token: string;
  expiresAt: number;
  actionClass: StepUpActionClass;
}

export async function postAdminStepUp(opts: {
  actionClass: StepUpActionClass;
  password: string;
  factor?: 'password';
}): Promise<StepUpResponse> {
  const { data } = await api.post<Envelope<StepUpResponse>>('/admin/console/step-up', opts);
  return unwrap(data);
}

// ------------------ Team management ------------------

export async function listAdminTeam(): Promise<{ members: SuperAdminMember[]; version: number; updatedAt: number }> {
  const { data } = await api.get<Envelope<{ members: SuperAdminMember[]; version: number; updatedAt: number }>>(
    '/admin/console/team'
  );
  return unwrap(data);
}

export async function promoteAdminMember(
  body: { identifier: string; role: SuperAdminRole; reason?: string; dryRun?: boolean },
  danger?: DangerOpts
): Promise<{ member?: SuperAdminMember; dryRun?: boolean; diff?: { before: unknown; after: unknown } }> {
  const { data } = await api.post<Envelope<{ member?: SuperAdminMember; dryRun?: boolean; diff?: { before: unknown; after: unknown } }>>(
    '/admin/console/team/promote',
    body,
    { headers: dangerHeaders(danger) }
  );
  return unwrap(data);
}

export async function demoteAdminMember(
  body: { uid: string; reason?: string; dryRun?: boolean },
  danger?: DangerOpts
): Promise<{ removed?: SuperAdminMember; remainingOwners?: number; dryRun?: boolean; diff?: { before: unknown; after: unknown } }> {
  const { data } = await api.post<
    Envelope<{
      removed?: SuperAdminMember;
      remainingOwners?: number;
      dryRun?: boolean;
      diff?: { before: unknown; after: unknown };
    }>
  >('/admin/console/team/demote', body, { headers: dangerHeaders(danger) });
  return unwrap(data);
}

export async function signOutAdminMember(
  body: { uid: string; reason?: string },
  danger: DangerOpts
): Promise<{ signedOutUid: string }> {
  const { data } = await api.post<Envelope<{ signedOutUid: string }>>(
    '/admin/console/team/signout',
    body,
    { headers: dangerHeaders(danger) }
  );
  return unwrap(data);
}

// ------------------ Two-person pending action queue ------------------

export async function listPendingAdminActions(): Promise<{ items: AdminPendingAction[] }> {
  const { data } = await api.get<Envelope<{ items: AdminPendingAction[] }>>('/admin/console/pending');
  return unwrap(data);
}

export async function proposePendingAction(body: {
  actionClass: StepUpActionClass;
  summary: string;
  payload: Record<string, unknown>;
  reason?: string;
}): Promise<{ pending: AdminPendingAction }> {
  const { data } = await api.post<Envelope<{ pending: AdminPendingAction }>>(
    '/admin/console/pending/propose',
    body
  );
  return unwrap(data);
}

export async function approvePendingAction(id: string): Promise<{ pending: AdminPendingAction }> {
  const { data } = await api.post<Envelope<{ pending: AdminPendingAction }>>(
    `/admin/console/pending/${encodeURIComponent(id)}/approve`,
    {}
  );
  return unwrap(data);
}

export async function rejectPendingAction(id: string, reason?: string): Promise<{ pending: AdminPendingAction }> {
  const { data } = await api.post<Envelope<{ pending: AdminPendingAction }>>(
    `/admin/console/pending/${encodeURIComponent(id)}/reject`,
    { reason }
  );
  return unwrap(data);
}

export interface ListAuditFilters {
  actorUid?: string;
  action?: string;
  targetAid?: string;
  sinceMs?: number;
  limit?: number;
}

// ------------------ Slice 1 — Accounts ------------------

export type AdminAccountSearchHitKind = 'email' | 'uid' | 'aid' | 'publicHandle';

export interface AdminAccountSearchHit {
  aid: string;
  matchedUid?: string;
  name?: string;
  email?: string;
  publicHandle?: string;
  hitKind: AdminAccountSearchHitKind;
}

export interface AdminAccountMember {
  uid: string;
  email?: string;
  displayName?: string;
  status?: boolean;
  isAccountAdmin: boolean;
  disabled?: boolean;
  emailVerified?: boolean;
  mfaEnrolled?: boolean;
  lastSignInMs?: number;
  createdMs?: number;
}

export interface AdminAccountSnapshot {
  aid: string;
  account: {
    name?: string;
    adminId?: string;
    publicHandle?: string;
    publicBio?: string;
    discoverable?: boolean;
    plans?: number;
    deletionRequestedAt?: number;
    scheduledDeletionAt?: number;
    deletionUndoUntil?: number;
    [k: string]: unknown;
  };
  members: AdminAccountMember[];
  subscription?: {
    planId: number;
    planKey: string;
    status: string;
    billingCycle?: string;
    currency?: string;
    currentPeriodEnd?: number | null;
    cancelAtPeriodEnd?: boolean;
    canceledAt?: number | null;
    trialEnd?: number;
    gracePeriodEndsAt?: number | null;
    readOnlyMode?: boolean;
    pauseUntil?: number | null;
  };
  byok?: {
    enabled?: boolean;
    provider?: string;
    keyLast4?: string;
    endpoint?: string;
    validatedAt?: number | null;
    updatedAt?: number | null;
  } | null;
  pendingDeletion?: {
    requestedAt?: number;
    scheduledAt?: number;
    undoUntil?: number;
  } | null;
}

export async function searchAdminAccounts(q: string, limit?: number): Promise<{ hits: AdminAccountSearchHit[] }> {
  const params: Record<string, string> = { q };
  if (limit) params.limit = String(limit);
  const { data } = await api.get<Envelope<{ hits: AdminAccountSearchHit[] }>>('/admin/console/accounts/search', {
    params,
  });
  return unwrap(data);
}

export async function getAdminAccount(aid: string): Promise<{ snapshot: AdminAccountSnapshot }> {
  const { data } = await api.get<Envelope<{ snapshot: AdminAccountSnapshot }>>(
    `/admin/console/accounts/${encodeURIComponent(aid)}`
  );
  return unwrap(data);
}

export async function adminAccountSignOut(
  aid: string,
  body: { reason?: string; dryRun?: boolean },
  danger?: DangerOpts
): Promise<{ signedOutUids?: string[]; dryRun?: boolean; wouldSignOut?: string[] }> {
  const { data } = await api.post<
    Envelope<{ signedOutUids?: string[]; dryRun?: boolean; wouldSignOut?: string[] }>
  >(`/admin/console/accounts/${encodeURIComponent(aid)}/sign-out`, body, {
    headers: dangerHeaders(danger),
  });
  return unwrap(data);
}

export async function adminAccountPasswordReset(
  aid: string,
  body: { reason?: string; targetUid?: string; dryRun?: boolean },
  danger?: DangerOpts
): Promise<{
  targetUid?: string;
  email?: string;
  resetLink?: string;
  dryRun?: boolean;
  wouldResetUid?: string;
}> {
  const { data } = await api.post<
    Envelope<{ targetUid?: string; email?: string; resetLink?: string; dryRun?: boolean; wouldResetUid?: string }>
  >(`/admin/console/accounts/${encodeURIComponent(aid)}/password-reset`, body, {
    headers: dangerHeaders(danger),
  });
  return unwrap(data);
}

export interface AdminEmailMigrationCounts {
  sharedIndexDocs: number;
  sharesOwnerEmail: number;
  sharesMemberEmail: number;
  sessionsSharedWith: number;
  tasksAssignee: number;
  billingCustomers: number;
  betaAllowList: number;
  userInvite: number;
  superAdminAllowlist: number;
  legacyGroupTroveShared: number;
  userSharesBackfill: number;
}

export interface AdminEmailChangePreview {
  uid: string;
  oldEmail: string;
  newEmail: string;
  emailVerified: boolean;
  providers: string[];
  federatedProviders: string[];
  isSuperAdmin: boolean;
  migrationCounts: AdminEmailMigrationCounts;
  warnings: string[];
  truncated: boolean;
  opId: string;
}

export interface AdminEmailChangeResult {
  uid: string;
  oldEmail: string;
  newEmail: string;
  migrationSummary: AdminEmailMigrationCounts;
  verificationLink: string;
  verificationEmailSent: boolean;
  oldEmailNoticeSent: boolean;
  truncated: boolean;
  cached?: boolean;
}

export type AdminEmailChangeBody = {
  newEmail: string;
  reason: string;
  opId: string;
  oauthAcknowledged?: boolean;
};

export async function adminUserEmailChangePreview(
  uid: string,
  body: Pick<AdminEmailChangeBody, 'newEmail' | 'reason' | 'opId'>
): Promise<AdminEmailChangePreview> {
  const { data } = await api.post<Envelope<AdminEmailChangePreview>>(
    `/admin/console/users/${encodeURIComponent(uid)}/email-change/preview`,
    body
  );
  return unwrap(data);
}

export async function adminUserEmailChange(
  uid: string,
  body: AdminEmailChangeBody,
  danger?: DangerOpts
): Promise<AdminEmailChangeResult> {
  const { data } = await api.post<Envelope<AdminEmailChangeResult>>(
    `/admin/console/users/${encodeURIComponent(uid)}/email-change`,
    body,
    { headers: dangerHeaders(danger) }
  );
  return unwrap(data);
}

export async function adminMemberEmailChangePreview(
  aid: string,
  uid: string,
  body: Pick<AdminEmailChangeBody, 'newEmail' | 'reason' | 'opId'>
): Promise<AdminEmailChangePreview> {
  const { data } = await api.post<Envelope<AdminEmailChangePreview>>(
    `/admin/console/accounts/${encodeURIComponent(aid)}/members/${encodeURIComponent(uid)}/email-change/preview`,
    body
  );
  return unwrap(data);
}

export async function adminMemberEmailChange(
  aid: string,
  uid: string,
  body: AdminEmailChangeBody,
  danger?: DangerOpts
): Promise<AdminEmailChangeResult> {
  const { data } = await api.post<Envelope<AdminEmailChangeResult>>(
    `/admin/console/accounts/${encodeURIComponent(aid)}/members/${encodeURIComponent(uid)}/email-change`,
    body,
    { headers: dangerHeaders(danger) }
  );
  return unwrap(data);
}

export async function adminAccountDiscover(
  aid: string,
  body: {
    publicHandle?: string | null;
    publicBio?: string | null;
    discoverable?: boolean | null;
    reason?: string;
    dryRun?: boolean;
  },
  danger?: DangerOpts
): Promise<{
  diff?: { before: Record<string, unknown>; after: Record<string, unknown> };
  dryRun?: boolean;
}> {
  const { data } = await api.post<
    Envelope<{
      diff?: { before: Record<string, unknown>; after: Record<string, unknown> };
      dryRun?: boolean;
    }>
  >(`/admin/console/accounts/${encodeURIComponent(aid)}/discover`, body, {
    headers: dangerHeaders(danger),
  });
  return unwrap(data);
}

export async function adminAccountDeletionCancel(
  aid: string,
  body: { reason?: string; dryRun?: boolean },
  danger?: DangerOpts
): Promise<{ canceled?: boolean; before?: Record<string, unknown> | null; dryRun?: boolean }> {
  const { data } = await api.post<
    Envelope<{ canceled?: boolean; before?: Record<string, unknown> | null; dryRun?: boolean }>
  >(`/admin/console/accounts/${encodeURIComponent(aid)}/deletion/cancel`, body, {
    headers: dangerHeaders(danger),
  });
  return unwrap(data);
}

// ------------------ Slice 2 — Billing ------------------

export interface AdminBillingOverview {
  aid: string;
  customer: {
    aid: string;
    email: string;
    customerId: string;
    defaultCurrency: string;
    createdAt: number;
    updatedAt: number;
  } | null;
  subscription: {
    aid: string;
    planId: number;
    planKey: string;
    status: string;
    billingCycle: string;
    currency: string;
    customerId: string;
    subscriptionId: string;
    priceId: string;
    currentPeriodEnd: number | null;
    cancelAtPeriodEnd: boolean;
    canceledAt: number | null;
    trialEnd?: number | null;
    trialUsed?: boolean;
    gracePeriodEndsAt?: number | null;
    readOnlyMode?: boolean;
    pauseUntil?: number | null;
    [k: string]: unknown;
  } | null;
  invoices: Array<{
    invoiceId: string;
    aid: string;
    subscriptionId: string;
    amount: number;
    currency: string;
    status: string;
    hostedInvoiceUrl: string;
    createdAt: number;
    paidAt: number | null;
    refundedAt: number | null;
    reason?: string;
  }>;
}

export async function getAdminBillingOverview(aid: string): Promise<{ overview: AdminBillingOverview }> {
  const { data } = await api.get<Envelope<{ overview: AdminBillingOverview }>>(
    `/admin/console/accounts/${encodeURIComponent(aid)}/billing`
  );
  return unwrap(data);
}

export async function adminBillingChangePlan(
  aid: string,
  body: { planId: number; keepStatus?: boolean; reason?: string; dryRun?: boolean },
  danger?: DangerOpts
): Promise<{ result?: unknown; dryRun?: boolean; before?: unknown }> {
  const { data } = await api.post<Envelope<{ result?: unknown; dryRun?: boolean; before?: unknown }>>(
    `/admin/console/accounts/${encodeURIComponent(aid)}/billing/plan`,
    body,
    { headers: dangerHeaders(danger) }
  );
  return unwrap(data);
}

export async function adminBillingRefund(
  aid: string,
  body: { invoiceId: string; reason?: string; dryRun?: boolean },
  danger?: DangerOpts
): Promise<{
  result?: { invoiceId: string; before: { status: string }; after: { status: string } };
  dryRun?: boolean;
}> {
  const { data } = await api.post<
    Envelope<{
      result?: { invoiceId: string; before: { status: string }; after: { status: string } };
      dryRun?: boolean;
    }>
  >(`/admin/console/accounts/${encodeURIComponent(aid)}/billing/refund`, body, {
    headers: dangerHeaders(danger),
  });
  return unwrap(data);
}

export async function adminBillingExtendTrial(
  aid: string,
  body: { addDays: number; restartIfExpired?: boolean; force?: boolean; reason?: string; dryRun?: boolean },
  danger?: DangerOpts
): Promise<{ result?: { before: unknown; after: unknown }; dryRun?: boolean; before?: unknown }> {
  const { data } = await api.post<
    Envelope<{ result?: { before: unknown; after: unknown }; dryRun?: boolean; before?: unknown }>
  >(`/admin/console/accounts/${encodeURIComponent(aid)}/billing/extend-trial`, body, {
    headers: dangerHeaders(danger),
  });
  return unwrap(data);
}

// ------------------ Slice 2 — Schedulers ------------------

export type AdminSchedulerName =
  | 'expireTrials'
  | 'advanceLifecycleStates'
  | 'vaultExcessOnDowngrade'
  | 'purgeExpiredVaults'
  | 'aiAbuseSweep'
  | 'sendWeeklyDigests';

export interface AdminSchedulerResult {
  name: AdminSchedulerName;
  startedAt: number;
  finishedAt: number;
  durationMs: number;
  summary: Record<string, unknown>;
}

export async function listAdminSchedulers(): Promise<{ schedulers: AdminSchedulerName[] }> {
  const { data } = await api.get<Envelope<{ schedulers: AdminSchedulerName[] }>>('/admin/console/schedulers');
  return unwrap(data);
}

export async function runAdminScheduler(
  body: { name: AdminSchedulerName; reason?: string; dryRun?: boolean },
  danger?: DangerOpts
): Promise<{ result?: AdminSchedulerResult; dryRun?: boolean; wouldRun?: AdminSchedulerName }> {
  const { data } = await api.post<
    Envelope<{ result?: AdminSchedulerResult; dryRun?: boolean; wouldRun?: AdminSchedulerName }>
  >('/admin/console/schedulers/run', body, { headers: dangerHeaders(danger) });
  return unwrap(data);
}

// ------------------ Slice 3 — AI ops ------------------

export interface AdminAiUsageSummaryRow {
  kind: string;
  count: number;
  embedTotalTokens: number;
  chatPromptTokens: number;
  chatCompletionTokens: number;
  estimatedUsd: number;
}

export interface AdminAiAccountOverview {
  aid: string;
  monthKey: string;
  throttled: boolean;
  throttleMeta?: {
    aiThrottledReason?: string;
    aiThrottledAt?: number;
    aiThrottledClearedAt?: number;
  };
  summary: AdminAiUsageSummaryRow[];
  byok?: {
    enabled?: boolean;
    provider?: string;
    keyLast4?: string;
    endpoint?: string;
    validatedAt?: number | null;
    updatedAt?: number | null;
  } | null;
}

export async function getAdminAiOverview(aid: string, monthKey?: string): Promise<{ overview: AdminAiAccountOverview }> {
  const params: Record<string, string> = {};
  if (monthKey) params.monthKey = monthKey;
  const { data } = await api.get<Envelope<{ overview: AdminAiAccountOverview }>>(
    `/admin/console/accounts/${encodeURIComponent(aid)}/ai`,
    { params }
  );
  return unwrap(data);
}

export interface AdminAiUsageItem {
  id: string;
  data: Record<string, unknown>;
}

export async function listAdminAiUsage(
  aid: string,
  opts: { monthKey?: string; cursor?: string; limit?: number } = {}
): Promise<{ items: AdminAiUsageItem[]; nextCursor: string | null; monthKey: string }> {
  const params: Record<string, string> = {};
  if (opts.monthKey) params.monthKey = opts.monthKey;
  if (opts.cursor) params.cursor = opts.cursor;
  if (opts.limit) params.limit = String(opts.limit);
  const { data } = await api.get<Envelope<{ items: AdminAiUsageItem[]; nextCursor: string | null; monthKey: string }>>(
    `/admin/console/accounts/${encodeURIComponent(aid)}/ai/usage`,
    { params }
  );
  return unwrap(data);
}

export async function adminAiThrottle(
  aid: string,
  body: { throttled: boolean; reason?: string; dryRun?: boolean },
  danger?: DangerOpts
): Promise<{ result?: { before: { throttled: boolean }; after: { throttled: boolean } }; dryRun?: boolean }> {
  const { data } = await api.post<
    Envelope<{ result?: { before: { throttled: boolean }; after: { throttled: boolean } }; dryRun?: boolean }>
  >(`/admin/console/accounts/${encodeURIComponent(aid)}/ai/throttle`, body, {
    headers: dangerHeaders(danger),
  });
  return unwrap(data);
}

export async function adminAiAbuseSweep(
  body: { reason?: string; dryRun?: boolean } = {},
  danger?: DangerOpts
): Promise<{
  summary?: { flagged: number; scanned: number };
  startedAt?: number;
  finishedAt?: number;
  durationMs?: number;
  dryRun?: boolean;
}> {
  const { data } = await api.post<
    Envelope<{
      summary?: { flagged: number; scanned: number };
      startedAt?: number;
      finishedAt?: number;
      durationMs?: number;
      dryRun?: boolean;
    }>
  >('/admin/console/ai/abuse-sweep', body, { headers: dangerHeaders(danger) });
  return unwrap(data);
}

// ------------------ Slice 4 — Public API + webhooks ------------------

export interface AdminApiKeyRow {
  id: string;
  aid: string;
  label: string;
  previewPrefix: string;
  createdAt: number;
  lastUsedAt?: number | null;
}

export interface AdminPublicApiUsage {
  aid: string;
  month: string | null;
  count: number;
  updatedAt?: number | null;
}

export async function listAdminApiKeys(
  aid: string,
  withUsage = true
): Promise<{ keys: AdminApiKeyRow[]; usage?: AdminPublicApiUsage }> {
  const params: Record<string, string> = {};
  if (withUsage) params.usage = '1';
  const { data } = await api.get<Envelope<{ keys: AdminApiKeyRow[]; usage?: AdminPublicApiUsage }>>(
    `/admin/console/accounts/${encodeURIComponent(aid)}/api-keys`,
    { params }
  );
  return unwrap(data);
}

export async function adminApiKeyRevoke(
  aid: string,
  keyId: string,
  body: { reason?: string; dryRun?: boolean },
  danger?: DangerOpts
): Promise<{ revoked?: boolean; dryRun?: boolean; wouldRevokeKeyId?: string }> {
  const { data } = await api.post<
    Envelope<{ revoked?: boolean; dryRun?: boolean; wouldRevokeKeyId?: string }>
  >(
    `/admin/console/accounts/${encodeURIComponent(aid)}/api-keys/${encodeURIComponent(keyId)}/revoke`,
    body,
    { headers: dangerHeaders(danger) }
  );
  return unwrap(data);
}

export async function adminApiKeyRotate(
  aid: string,
  keyId: string,
  body: { reason?: string },
  danger: DangerOpts
): Promise<{ oldKeyId: string; newKeyId: string; label: string; plaintext: string }> {
  const { data } = await api.post<
    Envelope<{ oldKeyId: string; newKeyId: string; label: string; plaintext: string }>
  >(
    `/admin/console/accounts/${encodeURIComponent(aid)}/api-keys/${encodeURIComponent(keyId)}/rotate`,
    body,
    { headers: dangerHeaders(danger) }
  );
  return unwrap(data);
}

export interface AdminWebhookRow {
  id: string;
  url: string;
  events: string[];
  createdAt: number;
}

export async function listAdminWebhooks(aid: string): Promise<{ webhooks: AdminWebhookRow[] }> {
  const { data } = await api.get<Envelope<{ webhooks: AdminWebhookRow[] }>>(
    `/admin/console/accounts/${encodeURIComponent(aid)}/webhooks`
  );
  return unwrap(data);
}

export async function adminWebhookTest(
  aid: string,
  webhookId: string,
  body: { reason?: string },
  danger: DangerOpts
): Promise<{
  result: {
    webhookId: string;
    url: string;
    ok: boolean;
    httpStatus?: number;
    durationMs: number;
    error?: string;
  };
}> {
  const { data } = await api.post<
    Envelope<{
      result: {
        webhookId: string;
        url: string;
        ok: boolean;
        httpStatus?: number;
        durationMs: number;
        error?: string;
      };
    }>
  >(
    `/admin/console/accounts/${encodeURIComponent(aid)}/webhooks/${encodeURIComponent(webhookId)}/test`,
    body,
    { headers: dangerHeaders(danger) }
  );
  return unwrap(data);
}

// ------------------ Slice 5 — Vault & content moderation ------------------

export type AdminVaultKind = 'troves' | 'notes' | 'highlighter';

export interface AdminVaultItem {
  id: string;
  [key: string]: unknown;
}

export interface AdminVaultSummary {
  aid: string;
  troves: AdminVaultItem[];
  notes: AdminVaultItem[];
  highlighter: AdminVaultItem[];
}

export async function getAdminAccountVault(aid: string): Promise<{ vault: AdminVaultSummary }> {
  const { data } = await api.get<Envelope<{ vault: AdminVaultSummary }>>(
    `/admin/console/accounts/${encodeURIComponent(aid)}/vault`
  );
  return unwrap(data);
}

export async function adminVaultRestore(
  aid: string,
  body: { kind: AdminVaultKind; id: string; reason?: string },
  danger: DangerOpts
): Promise<{ restored: boolean; reason?: string }> {
  const { data } = await api.post<Envelope<{ restored: boolean; reason?: string }>>(
    `/admin/console/accounts/${encodeURIComponent(aid)}/vault/restore`,
    body,
    { headers: dangerHeaders(danger) }
  );
  return unwrap(data);
}

export async function adminVaultPurge(
  aid: string,
  body: { kind: AdminVaultKind; id: string; reason?: string; dryRun?: boolean },
  danger?: DangerOpts
): Promise<{ purged?: boolean; dryRun?: boolean; wouldPurge?: { kind: string; id: string } }> {
  const { data } = await api.post<
    Envelope<{ purged?: boolean; dryRun?: boolean; wouldPurge?: { kind: string; id: string } }>
  >(`/admin/console/accounts/${encodeURIComponent(aid)}/vault/purge`, body, {
    headers: dangerHeaders(danger),
  });
  return unwrap(data);
}

export async function adminVaultExtend(
  aid: string,
  body: { kind: AdminVaultKind; id: string; addDays: number; reason?: string },
  danger: DangerOpts
): Promise<{ before: number | null; after: number | null }> {
  const { data } = await api.post<Envelope<{ before: number | null; after: number | null }>>(
    `/admin/console/accounts/${encodeURIComponent(aid)}/vault/extend`,
    body,
    { headers: dangerHeaders(danger) }
  );
  return unwrap(data);
}

export interface AdminShareRow {
  id: string;
  ownerAid: string;
  name?: string;
  publicLinkId?: string;
  publicLinkEnabled?: boolean;
  troveId?: string;
  groupId?: string;
  noteId?: string;
  notesGroupId?: string;
  projectSid?: string;
  members?: unknown;
  createdAt?: number;
  updatedAt?: number;
}

export async function listAdminAccountShares(aid: string): Promise<{ shares: AdminShareRow[] }> {
  const { data } = await api.get<Envelope<{ shares: AdminShareRow[] }>>(
    `/admin/console/accounts/${encodeURIComponent(aid)}/shares`
  );
  return unwrap(data);
}

export async function adminShareRevoke(
  aid: string,
  shareId: string,
  body: { reason?: string },
  danger: DangerOpts
): Promise<{ revoked: boolean; shareId: string }> {
  const { data } = await api.post<Envelope<{ revoked: boolean; shareId: string }>>(
    `/admin/console/accounts/${encodeURIComponent(aid)}/shares/${encodeURIComponent(shareId)}/revoke`,
    body,
    { headers: dangerHeaders(danger) }
  );
  return unwrap(data);
}

export async function adminShareDelete(
  aid: string,
  shareId: string,
  body: { reason?: string },
  danger: DangerOpts
): Promise<{ deleted: boolean; shareId: string }> {
  const { data } = await api.post<Envelope<{ deleted: boolean; shareId: string }>>(
    `/admin/console/accounts/${encodeURIComponent(aid)}/shares/${encodeURIComponent(shareId)}/delete`,
    body,
    { headers: dangerHeaders(danger) }
  );
  return unwrap(data);
}

export interface AdminSmartCollectionRow {
  id: string;
  name: string;
  queryJson: string;
  createdAt: number;
  updatedAt: number;
}

export async function listAdminAccountSmartCollections(
  aid: string
): Promise<{ collections: AdminSmartCollectionRow[] }> {
  const { data } = await api.get<Envelope<{ collections: AdminSmartCollectionRow[] }>>(
    `/admin/console/accounts/${encodeURIComponent(aid)}/smart-collections`
  );
  return unwrap(data);
}

export async function adminSmartCollectionDelete(
  aid: string,
  id: string,
  body: { reason?: string },
  danger: DangerOpts
): Promise<{ deleted: boolean; id: string }> {
  const { data } = await api.post<Envelope<{ deleted: boolean; id: string }>>(
    `/admin/console/accounts/${encodeURIComponent(aid)}/smart-collections/${encodeURIComponent(id)}/delete`,
    body,
    { headers: dangerHeaders(danger) }
  );
  return unwrap(data);
}

// ------------------ Slice 6 — Abuse, blocklist, kill switches ------------------

export interface AdminFingerprintRow {
  id: string;
  lastSeenAt?: number;
  hitCount?: number;
  ipPrefix?: string;
  buckets?: Record<string, number>;
  notes?: string;
}

export async function listAdminFingerprints(opts: { limit?: number; minHits?: number } = {}): Promise<{
  fingerprints: AdminFingerprintRow[];
}> {
  const params: Record<string, string> = {};
  if (opts.limit) params.limit = String(opts.limit);
  if (opts.minHits) params.minHits = String(opts.minHits);
  const { data } = await api.get<Envelope<{ fingerprints: AdminFingerprintRow[] }>>(
    '/admin/console/abuse/fingerprints',
    { params }
  );
  return unwrap(data);
}

export async function getAdminFingerprint(id: string): Promise<{ fingerprint: AdminFingerprintRow }> {
  const { data } = await api.get<Envelope<{ fingerprint: AdminFingerprintRow }>>(
    `/admin/console/abuse/fingerprints/${encodeURIComponent(id)}`
  );
  return unwrap(data);
}

export type AdminBlockRuleKind = 'ip' | 'fingerprint' | 'email_domain';

export interface AdminBlockRule {
  id: string;
  kind: AdminBlockRuleKind;
  value: string;
  reason: string;
  createdBy: string;
  createdAt: number;
  expiresAt?: number | null;
  hardBlock: boolean;
}

export async function listAdminBlockRules(): Promise<{ rules: AdminBlockRule[] }> {
  const { data } = await api.get<Envelope<{ rules: AdminBlockRule[] }>>(
    '/admin/console/abuse/block-rules'
  );
  return unwrap(data);
}

export async function adminBlockRuleUpsert(
  body: {
    id?: string;
    kind: AdminBlockRuleKind;
    value: string;
    reason: string;
    hardBlock?: boolean;
    expiresAt?: number | null;
  },
  danger: DangerOpts
): Promise<{ rule: AdminBlockRule }> {
  const { data } = await api.post<Envelope<{ rule: AdminBlockRule }>>(
    '/admin/console/abuse/block-rules',
    body,
    { headers: dangerHeaders(danger) }
  );
  return unwrap(data);
}

export async function adminBlockRuleRemove(
  id: string,
  body: { reason?: string },
  danger: DangerOpts
): Promise<{ removed: boolean; id: string }> {
  const { data } = await api.post<Envelope<{ removed: boolean; id: string }>>(
    `/admin/console/abuse/block-rules/${encodeURIComponent(id)}/remove`,
    body,
    { headers: dangerHeaders(danger) }
  );
  return unwrap(data);
}

export async function adminAccountReadOnly(
  aid: string,
  body: { readOnly: boolean; reason?: string },
  danger: DangerOpts
): Promise<{
  aid: string;
  before: { readOnlyMode: boolean };
  after: { readOnlyMode: boolean };
}> {
  const { data } = await api.post<
    Envelope<{
      aid: string;
      before: { readOnlyMode: boolean };
      after: { readOnlyMode: boolean };
    }>
  >(`/admin/console/accounts/${encodeURIComponent(aid)}/read-only`, body, {
    headers: dangerHeaders(danger),
  });
  return unwrap(data);
}

export type AdminKillSwitchName = 'aiGlobal' | 'publicApiGlobal' | 'writesGlobal';

export interface AdminKillSwitchFlag {
  enabled: boolean;
  setBy?: string;
  setByEmail?: string;
  setAt?: number;
  reason?: string;
  pendingActionId?: string;
}

export interface AdminKillSwitchesDoc {
  flags: Partial<Record<AdminKillSwitchName, AdminKillSwitchFlag>>;
  updatedAt?: number;
  version?: number;
}

export async function getAdminKillSwitches(): Promise<{
  killSwitches: AdminKillSwitchesDoc;
  available: AdminKillSwitchName[];
}> {
  const { data } = await api.get<
    Envelope<{ killSwitches: AdminKillSwitchesDoc; available: AdminKillSwitchName[] }>
  >('/admin/console/kill-switches');
  return unwrap(data);
}

export async function adminKillSwitchPropose(body: {
  name: AdminKillSwitchName;
  enabled: boolean;
  reason: string;
}): Promise<{ pending: AdminPendingAction }> {
  const { data } = await api.post<Envelope<{ pending: AdminPendingAction }>>(
    '/admin/console/kill-switches/propose',
    body
  );
  return unwrap(data);
}

export async function adminKillSwitchExecute(
  body: { pendingId: string; reason?: string },
  danger: DangerOpts
): Promise<{
  result: { before?: AdminKillSwitchFlag; after: AdminKillSwitchFlag };
  pendingId: string;
}> {
  const { data } = await api.post<
    Envelope<{
      result: { before?: AdminKillSwitchFlag; after: AdminKillSwitchFlag };
      pendingId: string;
    }>
  >('/admin/console/kill-switches/execute', body, { headers: dangerHeaders(danger) });
  return unwrap(data);
}

// ------------------ Slice 7 — Beta / waitlist / feature flags ------------------

export type AdminWaitlistStatus = 'pending' | 'confirmed' | 'invited' | 'admitted' | 'revoked';

export interface AdminWaitlistEntry {
  id: string;
  email: string;
  emailHashSha256: string;
  source: string;
  status: AdminWaitlistStatus;
  surface: 'web' | 'mobile' | 'desktop' | 'all';
  createdAt: number;
  confirmedAt?: number;
  admittedAt?: number;
  notes?: string;
}

export interface AdminWaitlistGateInfo {
  enabled: boolean;
  envFlag: string;
}

export async function listAdminWaitlist(opts: {
  status?: AdminWaitlistStatus | 'all';
  limit?: number;
} = {}): Promise<{ entries: AdminWaitlistEntry[]; gate: AdminWaitlistGateInfo }> {
  const params: Record<string, string> = {};
  if (opts.status) params.status = opts.status;
  if (opts.limit) params.limit = String(opts.limit);
  const { data } = await api.get<
    Envelope<{ entries: AdminWaitlistEntry[]; gate: AdminWaitlistGateInfo }>
  >('/admin/console/waitlist', { params });
  return unwrap(data);
}

export async function getAdminWaitlistEntry(email: string): Promise<{ entry: AdminWaitlistEntry | null }> {
  const { data } = await api.get<Envelope<{ entry: AdminWaitlistEntry | null }>>(
    '/admin/console/waitlist/entry',
    { params: { email } }
  );
  return unwrap(data);
}

export async function adminWaitlistAdd(
  body: {
    email: string;
    surface?: 'web' | 'mobile' | 'desktop' | 'all';
    notes?: string;
    admit?: boolean;
    reason?: string;
  },
  danger: DangerOpts
): Promise<{
  status: 'pending_confirmation' | 'already_confirmed' | 'already_admitted' | 'revoked' | 'admitted';
  emailHashSha256: string;
  confirmToken?: string;
}> {
  const { data } = await api.post<
    Envelope<{
      status:
        | 'pending_confirmation'
        | 'already_confirmed'
        | 'already_admitted'
        | 'revoked'
        | 'admitted';
      emailHashSha256: string;
      confirmToken?: string;
    }>
  >('/admin/console/waitlist/add', body, { headers: dangerHeaders(danger) });
  return unwrap(data);
}

export async function adminWaitlistAdmit(
  body: { email: string; notes?: string; reason?: string },
  danger: DangerOpts
): Promise<{ status: 'admitted' | 'noop' }> {
  const { data } = await api.post<Envelope<{ status: 'admitted' | 'noop' }>>(
    '/admin/console/waitlist/admit',
    body,
    { headers: dangerHeaders(danger) }
  );
  return unwrap(data);
}

export async function adminWaitlistRevoke(
  body: { email: string; reason?: string },
  danger: DangerOpts
): Promise<{ status: 'revoked' }> {
  const { data } = await api.post<Envelope<{ status: 'revoked' }>>(
    '/admin/console/waitlist/revoke',
    body,
    { headers: dangerHeaders(danger) }
  );
  return unwrap(data);
}

export interface AdminFeatureFlagValue {
  enabled: boolean;
  reason?: string;
  setBy?: string;
  setByEmail?: string;
  setAt?: number;
  aid?: string;
}

export interface AdminFeatureFlagsDoc {
  flags: Record<string, AdminFeatureFlagValue>;
  updatedAt?: number;
  version?: number;
}

export async function listAdminFeatureFlags(): Promise<{ flags: AdminFeatureFlagsDoc }> {
  const { data } = await api.get<Envelope<{ flags: AdminFeatureFlagsDoc }>>(
    '/admin/console/feature-flags'
  );
  return unwrap(data);
}

export async function adminFeatureFlagSet(
  body: { name: string; enabled: boolean; reason?: string; aid?: string },
  danger: DangerOpts
): Promise<{ before?: AdminFeatureFlagValue; after: AdminFeatureFlagValue }> {
  const { data } = await api.post<
    Envelope<{ before?: AdminFeatureFlagValue; after: AdminFeatureFlagValue }>
  >('/admin/console/feature-flags/set', body, { headers: dangerHeaders(danger) });
  return unwrap(data);
}

export async function adminFeatureFlagRemove(
  body: { name: string; reason?: string; aid?: string },
  danger: DangerOpts
): Promise<{ removed: boolean }> {
  const { data } = await api.post<Envelope<{ removed: boolean }>>(
    '/admin/console/feature-flags/remove',
    body,
    { headers: dangerHeaders(danger) }
  );
  return unwrap(data);
}

// ------------------ Slice 8 — Health / jobs ------------------

export interface AdminHealthSchedulerRow {
  name:
    | 'expireTrials'
    | 'advanceLifecycleStates'
    | 'vaultExcessOnDowngrade'
    | 'purgeExpiredVaults'
    | 'aiAbuseSweep'
    | 'sendWeeklyDigests';
  lastAdminRunAt: number | null;
  lastAdminRunActor?: string;
  lastAdminRunOutcome?: 'success' | 'error';
}

export interface AdminHealthSummary {
  serverTime: number;
  build: {
    sha?: string;
    version?: string;
    revision?: string;
    nodeEnv?: string;
    region?: string;
  };
  schedulers: AdminHealthSchedulerRow[];
  pendingActionsCount: number;
  audit: { last24hCount: number; last7dCount: number };
  billing: { subscriptions: Array<{ status: string; count: number }> };
}

export async function getAdminHealth(): Promise<{ health: AdminHealthSummary }> {
  const { data } = await api.get<Envelope<{ health: AdminHealthSummary }>>('/admin/console/health');
  return unwrap(data);
}

// ------------------ Audit log ------------------

export async function listAdminAudit(filters: ListAuditFilters = {}): Promise<{ items: AdminAuditLogEntry[] }> {
  const params: Record<string, string> = {};
  if (filters.actorUid) params.actorUid = filters.actorUid;
  if (filters.action) params.action = filters.action;
  if (filters.targetAid) params.targetAid = filters.targetAid;
  if (filters.sinceMs) params.sinceMs = String(filters.sinceMs);
  if (filters.limit) params.limit = String(filters.limit);
  const { data } = await api.get<Envelope<{ items: AdminAuditLogEntry[] }>>('/admin/console/audit', { params });
  return unwrap(data);
}
